import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, signal } from "@angular/core";
import { AppMessagesComponent } from '@common/messages/frontend-shared';
import { UiButtonComponent } from '@common/ui/frontend-shared/button';
import { UiContentCenteredComponent } from '@common/ui/frontend-shared/content-centered';
import { UiIconsModule } from '@common/ui/frontend-shared/icons';
import { UiLogoComponent } from '@common/ui/frontend-shared/logo/logo';
import { AccessFacade, AppRequestService, ConfigService, LocalMessageHandlerService } from '@core/frontend-shared';
import { AppState, AppStateFacade } from '@core/frontend-shared/app-state';
import { I18nService, I18nState } from '@core/frontend-shared/i18n';
import { PingServerRequest } from '@core/shared/requests';
import { Actions, Store } from '@ngxs/store';
import * as i0 from "@angular/core";
import * as i1 from "@core/frontend-shared";
import * as i2 from "@core/frontend-shared/app-state";
import * as i3 from "@ngxs/store";
import * as i4 from "@core/frontend-shared/i18n";
import * as i5 from "../../../../../ui/frontend-shared/src/lib/icons/icon.component";
import * as i6 from "@angular/common";
function CoreConnectionErrorComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 0)(1, "div", 1)(2, "div", 2);
    i0.ɵɵelement(3, "ui-logo", 3);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "ui-content-centered")(5, "div")(6, "h1", 4);
    i0.ɵɵelement(7, "ui-icon", 5);
    i0.ɵɵtext(8);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(9, "p");
    i0.ɵɵtext(10);
    i0.ɵɵelementEnd();
    i0.ɵɵelement(11, "app-messages");
    i0.ɵɵelementStart(12, "p")(13, "button", 6);
    i0.ɵɵlistener("click", function CoreConnectionErrorComponent_Conditional_0_Template_button_click_13_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.clickRetry());
    });
    i0.ɵɵelementEnd()()()()()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("scaling", 100);
    i0.ɵɵadvance(5);
    i0.ɵɵtextInterpolate1(" ", ctx_r1.messageBoxTitle(), " ");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(ctx_r1.messageBoxContents());
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("loading", ctx_r1.loading());
  }
}
/**
 * error component used by access guard
 */
export let CoreConnectionErrorComponent = /*#__PURE__*/(() => {
  class CoreConnectionErrorComponent {
    constructor(access, appFacade, api, events, messages, i18n, config) {
      this.access = access;
      this.appFacade = appFacade;
      this.api = api;
      this.events = events;
      this.messages = messages;
      this.i18n = i18n;
      this.config = config;
      this.connectionError$ = inject(Store).select(AppState.connectionError);
      this.isAnyLanguageDataAvailable = inject(Store).selectSnapshot(I18nState.isAnyLanguageDataAvailable);
      // connectionError$ = of(true)
      this.loading = signal(false, ...(ngDevMode ? [{
        debugName: "loading"
      }] : /* istanbul ignore next */[]));
      // NOTE: special situation for i18n data:
      // in case of connection error on app startup, no i18n data will be available.
      // so we add a manual static fallback to cover that case too.
      this.messageBoxTitle = computed(() => {
        if (this.isAnyLanguageDataAvailable) {
          return this.i18n.translate('ERR_CONNECTION_SHORT');
        } else {
          return this.config.get('i18n.defaults.connectionErrorTitle', 'Connection Error');
        }
      }, ...(ngDevMode ? [{
        debugName: "messageBoxTitle"
      }] : /* istanbul ignore next */[]));
      this.messageBoxContents = computed(() => {
        if (this.isAnyLanguageDataAvailable) {
          return this.i18n.translate('ERR_CONNECTION_DESCR');
        } else {
          return this.config.get('i18n.defaults.connectionErrorInfo', 'The application failed to connect to the server. Please try again in a few minutes.');
        }
      }, ...(ngDevMode ? [{
        debugName: "messageBoxContents"
      }] : /* istanbul ignore next */[]));
    }
    clickRetry() {
      this.messages.clear();
      this.loading.set(true);
      this.api.sendRequest(PingServerRequest, {}).subscribe({
        // ApiErrorInterceptor creates no AppMessage on error 0, so error handler will receive
        // a httpErrorResponse(0) or an AppMessage in case of a different error code.
        error: error => {
          this.loading.set(false);
          if (error instanceof HttpErrorResponse) {
            this.messages.createAndDisplay(this.messageBoxContents());
          } else {
            if (!this.isAnyLanguageDataAvailable) error.setMessage(this.messageBoxContents());
            this.messages.add(error);
          }
        },
        next: res => {
          this.loading.set(false);
          this.access.refreshSession();
          this.appFacade.setConnectionError(false);
        }
      });
      this.access.refreshSession();
    }
    static {
      this.ɵfac = function CoreConnectionErrorComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CoreConnectionErrorComponent)(i0.ɵɵdirectiveInject(i1.AccessFacade), i0.ɵɵdirectiveInject(i2.AppStateFacade), i0.ɵɵdirectiveInject(i1.AppRequestService), i0.ɵɵdirectiveInject(i3.Actions), i0.ɵɵdirectiveInject(i1.LocalMessageHandlerService), i0.ɵɵdirectiveInject(i4.I18nService), i0.ɵɵdirectiveInject(i1.ConfigService));
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: CoreConnectionErrorComponent,
        selectors: [["core-connection-error"]],
        features: [i0.ɵɵProvidersFeature([LocalMessageHandlerService])],
        decls: 2,
        vars: 3,
        consts: [[1, "overlay"], [1, "infobox"], [1, "logowrapper"], ["background", "true", "id", "logo", 3, "scaling"], [1, "ui-margin-remove"], ["icon", "exclamation-triangle"], ["uiButton", "", "label", "Erneut versuchen", "icon", "sync-alt", 1, "ui-button-contrast", 3, "click", "loading"]],
        template: function CoreConnectionErrorComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵconditionalCreate(0, CoreConnectionErrorComponent_Conditional_0_Template, 14, 4, "div", 0);
            i0.ɵɵpipe(1, "async");
          }
          if (rf & 2) {
            i0.ɵɵconditional(i0.ɵɵpipeBind1(1, 1, ctx.connectionError$) ? 0 : -1);
          }
        },
        dependencies: [CommonModule, UiContentCenteredComponent, UiIconsModule, i5.UiIconComponent, UiButtonComponent, UiLogoComponent, AppMessagesComponent, i6.AsyncPipe],
        styles: [".overlay[_ngcontent-%COMP%]{position:fixed;inset:0;z-index:9998;background:var(--invertedOverlaySoftColor);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);transition:background-color .3s}.overlay[_ngcontent-%COMP%]{display:flex;justify-content:center;align-items:center}.infobox[_ngcontent-%COMP%]{background:var(--contentColor);color:var(--textColor);padding:20px 120px 20px 20px;width:600px;max-width:90%;position:relative}.infotext[_ngcontent-%COMP%]{margin:30px 0}.logowrapper[_ngcontent-%COMP%]{position:absolute;width:100px;right:0;top:0}"],
        changeDetection: 0
      });
    }
  }
  return CoreConnectionErrorComponent;
})();